import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { ghostButtonClass, primaryButtonClass } from "@/components/base/fieldStyles";
import { formatNumber } from "@/utils/format";
import { useApp } from "@/store/AppStore";
import {
  listAvailableTables,
  previewRestore,
  type BackupKey,
  type BackupPayload,
  type RestoreSummary,
} from "@/utils/jsonBackup";

interface Props {
  open: boolean;
  payload: BackupPayload | null;
  onClose: () => void;
  onDone: (summary: RestoreSummary) => void;
}

// Modal "Elige qué restaurar": muestra solo las categorías que vienen en el
// archivo, con checkboxes, vista previa y confirmación no destructiva.
const RestoreSelectModal = ({ open, payload, onClose, onDone }: Props) => {
  const { data, restoreBackup } = useApp();
  const available = useMemo(() => (payload ? listAvailableTables(payload) : []), [payload]);
  const [selected, setSelected] = useState<Set<BackupKey>>(new Set());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && payload) {
      setSelected(new Set(listAvailableTables(payload).map((t) => t.key)));
      setError("");
    }
  }, [open, payload]);

  const selection = useMemo(() => Array.from(selected), [selected]);
  const preview = useMemo(
    () => (payload ? previewRestore(payload, data, selection) : []),
    [payload, data, selection]
  );
  const previewByKey = useMemo(() => new Map(preview.map((r) => [r.key, r])), [preview]);
  const totalNew = preview.reduce((acc, r) => acc + r.newCount, 0);
  const totalDuplicate = preview.reduce((acc, r) => acc + r.duplicates, 0);
  const totalSkipped = preview.reduce((acc, r) => acc + r.skipped, 0);

  const allSelected = available.length > 0 && available.every((t) => selected.has(t.key));

  const toggle = (key: BackupKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(available.map((t) => t.key)));
  };

  const confirm = async () => {
    if (!payload) return;
    if (selection.length === 0) {
      setError("No seleccionaste ningún módulo para restaurar.");
      return;
    }
    setWorking(true);
    setError("");
    const summary = await restoreBackup(payload, selection);
    setWorking(false);
    if (summary) {
      onDone(summary);
      onClose();
    } else {
      setError("No se pudo completar la restauración. Intenta de nuevo.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !working && onClose()}
      title="Elige qué restaurar"
      icon="ri-upload-cloud-2-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} disabled={working} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={confirm} disabled={working} className={primaryButtonClass}>
            <i className="ri-download-2-line" />{" "}
            {working ? "Restaurando..." : `Restaurar (${formatNumber(totalNew)})`}
          </button>
        </>
      }
    >
      <div className="rounded-md bg-secondary-500/15 border border-secondary-500/25 px-3 py-2 mb-4 flex items-start gap-2">
        <i className="ri-information-line text-secondary-200 mt-0.5" />
        <p className="text-[11px] text-foreground-700">
          La restauración <strong className="text-foreground-900">solo agrega</strong> lo que te
          falta: nunca borra ni sobrescribe. Los registros que ya tienes se omiten.
        </p>
      </div>

      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold text-foreground-600">
          Categorías encontradas en el archivo
        </p>
        <button
          onClick={toggleAll}
          className="text-[11px] font-semibold text-accent-200 hover:text-accent-100 cursor-pointer whitespace-nowrap"
        >
          {allSelected ? "Quitar todo" : "Seleccionar todo"}
        </button>
      </div>

      <div className="rounded-md border border-background-300/40 divide-y divide-background-300/30 max-h-[24rem] overflow-y-auto">
        {available.map((t) => {
          const checked = selected.has(t.key);
          const p = previewByKey.get(t.key);
          const newCount = p?.newCount ?? 0;
          const duplicates = p?.duplicates ?? 0;
          const skipped = p?.skipped ?? 0;
          const alreadyHasAll = checked && newCount === 0;
          return (
            <label
              key={t.key}
              className={`flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors ${
                checked ? "bg-primary-500/10" : "hover:bg-background-200/40"
              }`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(t.key)}
                className="w-4 h-4 mt-0.5 accent-primary-500 cursor-pointer shrink-0"
              />
              <span className="w-8 h-8 rounded-md bg-background-200/60 text-accent-200 flex items-center justify-center shrink-0">
                <i className={t.icon} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-foreground-900 truncate">
                    {t.label}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-200/60 text-foreground-600 whitespace-nowrap">
                    {formatNumber(t.count)} en el archivo
                  </span>
                </span>
                <span className="block text-[10px] text-foreground-500 mt-0.5">
                  {checked ? (
                    alreadyHasAll ? (
                      <span className="text-accent-200">Ya tienes todo, no hay nada nuevo.</span>
                    ) : (
                      <>
                        <strong className="text-foreground-800">{formatNumber(newCount)}</strong>{" "}
                        nuevo(s)
                        {duplicates > 0 && ` · ${formatNumber(duplicates)} ya existen`}
                        {skipped > 0 && ` · ${formatNumber(skipped)} inválido(s)`}
                      </>
                    )
                  ) : (
                    "Sin seleccionar"
                  )}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      <div className="mt-3 rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2">
        <p className="text-[11px] text-foreground-700">
          Seleccionaste <strong className="text-foreground-900">{formatNumber(selection.length)}</strong>{" "}
          categoría(s). Se agregarán{" "}
          <strong className="text-accent-200">{formatNumber(totalNew)}</strong> registro(s).
          {totalDuplicate > 0 && ` ${formatNumber(totalDuplicate)} ya existen y se omitirán.`}
          {totalSkipped > 0 && ` ${formatNumber(totalSkipped)} inválido(s) se omitirán.`}
        </p>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5 animate-fade-up">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default RestoreSelectModal;